const { trapFocus, disableTrapFocus } = window.BAO.Utils
export class ShippingSelector extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.countryCode = this.noLocationStored
      ? this.dataset.store
      : window.localStorage.getItem(this.userLocationName)

    // eslint-disable-next-line compat/compat
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    })
    const countryParam = params.country

    if (countryParam !== null) this.countryCode = countryParam

    this.updateShippingLabel(this.countryCode)

    this.hasOptions = false
    this.optionsHtml = ''
  }

  connectedCallback () {
    super.connectedCallback()

    this.constructOptionsHtml()
  }

  updateShippingLabel (countryCode) {
    if (this.noLocationStored) return

    const upperCountryCode = countryCode.toUpperCase()

    if (this.countries[upperCountryCode] !== undefined) {
      const name = this.countries[upperCountryCode].name
      const currencySymbol = this.countries[upperCountryCode].symbol

      document
        .querySelectorAll('[data-shipping-selector-external-el="label"]')
        .forEach(labelExternalEl => {
          labelExternalEl.innerHTML = `${name} / ${currencySymbol}`
        })

      document
        .querySelectorAll('[data-shipping-selector-external-el="mobile-label"]')
        .forEach(labelExternalEl => {
          labelExternalEl.innerHTML = `${labelExternalEl.dataset.label} ${name} / ${currencySymbol}`
        })
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onSelection = this.onSelection.bind(this)

    if (this.els?.items.exists) {
      this.els.items.elements.forEach(itemsEl => {
        this.listeners.add(itemsEl, 'click', this.onSelection)
      })
    }

    this.onConfirmClick = this.onConfirmClick.bind(this)

    if (this.externalEls?.confirm.exists) {
      this.externalEls.confirm.elements.forEach(confirmExternalEl => {
        this.listeners.add(confirmExternalEl, 'click', this.onConfirmClick)
      })
    }

    this.onCloseClick = this.onCloseClick.bind(this)

    if (this.externalEls?.close.exists) {
      this.externalEls.close.elements.forEach(closeExternalEl => {
        this.listeners.add(closeExternalEl, 'click', this.onCloseClick)
      })
    }

    this.onSearch = this.onSearch.bind(this)
    this.onDrawerOpen = this.onDrawerOpen.bind(this)

    if (this.externalEls?.search.exists) {
      this.externalEls.search.elements.forEach(searchExternalEl => {
        this.listeners.add(searchExternalEl, 'input', this.onSearch)
      })

      this.listeners.add(document, 'drawerOpen', this.onDrawerOpen)
    }

    this.onResetClick = this.onResetClick.bind(this)

    if (this.externalEls?.reset.exists) {
      this.externalEls.reset.elements.forEach(resetExternalEl => {
        this.listeners.add(resetExternalEl, 'click', this.onResetClick)
      })
    }
  }

  onResetClick () {
    const items = this.querySelectorAll('.shp-ShippingSelector_Item')

    items.forEach(item => {
      item.style.display = ''
    })

    if (this.externalEls?.search.exists) {
      this.externalEls.search.elements.forEach(searchExternalEl => {
        searchExternalEl.value = ''
        searchExternalEl.focus()
      })
    }

    if (this.externalEls?.reset.exists) {
      this.externalEls.reset.elements.forEach(resetExternalEl => {
        resetExternalEl.setAttribute('aria-hidden', 'true')
        resetExternalEl.disabled = true
      })
    }
  }

  onDrawerOpen (event) {
    if (event.detail.key === 'shipping-selector') {
      this.createOptionsList()
      window.setTimeout(() => this.externalEls.search.element.focus(), 400)
    }
  }

  onSearch (event) {
    const searchValue = event.target.value.toLowerCase()

    const items = this.querySelectorAll('.shp-ShippingSelector_Item')

    items.forEach(item => {
      const itemName = item.querySelector('.shp-ShippingSelector_Name')
      if (!itemName) return

      let itemNameContent = item.querySelector('.shp-ShippingSelector_Name')
        .textContent
      const itemNameContentLower = itemNameContent.toLowerCase()

      itemName.innerHTML = itemName.dataset.name

      if (itemNameContentLower.indexOf(searchValue) > -1) {
        item.style.display = ''

        const itemIndex = itemNameContentLower.indexOf(searchValue)
        itemNameContent = `${itemNameContent.substring(
          0,
          itemIndex,
        )}<mark>${itemNameContent.substring(
          itemIndex,
          itemIndex + searchValue.length,
        )}</mark>${itemNameContent.substring(itemIndex + searchValue.length)}`
        itemName.innerHTML = itemNameContent
      } else {
        item.style.display = 'none'
      }
    })

    if (this.externalEls?.reset.exists) {
      this.externalEls.reset.elements.forEach(resetExternalEl => {
        resetExternalEl.setAttribute('aria-hidden', searchValue === '')
        resetExternalEl.disabled = searchValue === ''
      })
    }
  }

  onCloseClick (event) {
    event.preventDefault()

    if (this.externalEls?.popup.exists) {
      this.externalEls.popup.elements.forEach(popupExternalEl => {
        popupExternalEl.setAttribute('aria-hidden', true)
      })
    }

    // Refocus on the countries list drawer if it is open.
    if (window.drawers.activeDrawerKey === 'shipping-selector') {
      setTimeout(() => {
        const closeEl = document.querySelector(
          '[data-drawers-close="shipping-selector"]',
        )
        closeEl.focus()
      }, 250)

      setTimeout(() => {
        const element = document.querySelector(
          `site-drawer[key='shipping-selector']`,
        )

        disableTrapFocus()
        trapFocus(element)
      }, 500)
    }
  }

  onConfirmClick () {
    const searchParams = new URLSearchParams(window.location.search)

    const paramsObject = {}
    searchParams.forEach((value, key) => {
      paramsObject[key] = value
    })

    // Filter out parameters starting with "country"
    const filteredParamsObject = Object.fromEntries(
      Object.entries(paramsObject).filter(([key]) => !key.startsWith('country')),
    )

    const filteredSearchParams = new URLSearchParams(filteredParamsObject)
    const searchParamsString = filteredSearchParams.toString()

    const languageOptionsData = this.retrieveLanguageOptionData(
      this.countryCode,
    )

    if (this.sites[this.site].url !== undefined) {
      window.location.href = `${this.sites[this.site].url}${
        this.pathnameWithoutLanguageSubdirectory
      }?country=${this.countryCode}&${searchParamsString}&${
        this.sites[this.site].currency
      }&language_prompt=${languageOptionsData ? 'true' : 'false'}`
    }
  }

  onSelection (e) {
    if (!e.target.matches('[data-shipping-selector-el="button"]')) return

    const searchParams = new URLSearchParams(window.location.search)

    const paramsObject = {}
    searchParams.forEach((value, key) => {
      paramsObject[key] = value
    })

    // Filter out parameters starting with "country"
    const filteredParamsObject = Object.fromEntries(
      Object.entries(paramsObject).filter(([key]) => !key.startsWith('country')),
    )

    const filteredSearchParams = new URLSearchParams(filteredParamsObject)

    const searchParamsString = filteredSearchParams.toString()

    this.countryCode = e.target.dataset.code
    this.site = e.target.dataset.site

    const languageOptionsData = this.retrieveLanguageOptionData(
      this.countryCode,
    )

    // If clicking to external store
    if (
      this.site !== this.dataset.site &&
      this.sites[this.site].url !== this.sites[this.dataset.site].url
    ) {
      if (this.externalEls?.popup.exists) {
        this.externalEls.popup.elements.forEach(popupExternalEl => {
          popupExternalEl.setAttribute('aria-hidden', false)

          // Trap focus in popup.
          disableTrapFocus()
          trapFocus(popupExternalEl)

          setTimeout(() => {
            // Focus on 'proceed' button.
            popupExternalEl
              .querySelector('[data-shipping-selector-external-el="confirm"]')
              .focus()
          }, 500)
        })
      }
    } else {
      // If staying on same store
      const shippingSelectorUpdate = new CustomEvent('shippingSelectorUpdate', {
        detail: this.countryCode,
      })

      document.dispatchEvent(shippingSelectorUpdate)

      if (this.sites[this.site].url !== undefined) {
        const href = `${this.sites[this.site].url}${
          this.pathnameWithoutLanguageSubdirectory
        }?country=${this.countryCode}&${searchParamsString}&${
          this.sites[this.site].currency
        }&language_prompt=${languageOptionsData ? 'true' : 'false'}`
        window.location.href = href
      }
    }
  }

  constructOptionsHtml () {
    Object.entries(this.countries).forEach(([key, value]) => {
      const { currency, name, site } = value
      const isCurrentSite = key.toLowerCase() === this.countryCode.toLowerCase()

      this.optionsHtml += this.createOptionElement(
        key,
        site,
        isCurrentSite,
        name,
        currency,
      )
    })
  }

  createOptionsList () {
    if (
      !this.countryCode ||
      this.hasOptions === true ||
      this.optionsHtml === ''
    ) {
      return
    }

    this.els.items.element.insertAdjacentHTML('beforeend', this.optionsHtml)

    this.hasOptions = true
  }

  createOptionElement (code, site, isCurrentSite, country, currency) {
    return `<li class="shp-ShippingSelector_Item">
        <button class="shp-ShippingSelector_Button${
          isCurrentSite ? ' shp-ShippingSelector_Button-selected' : ''
        }" data-shipping-selector-el="button" data-code="${code}" data-site="${site}"${
      this.dataset.site === site ? ' data-drawers-close' : ''
    }>
          <span class="shp-ShippingSelector_Name" data-name="${country}">${country}</span>
          <span class="shp-ShippingSelector_Currency">${currency}</span>
        </button>
      </li>`
  }

  retrieveLanguageOptionData (key) {
    key = key.toUpperCase()

    if (
      this.sites[key] &&
      this.sites[key].languages &&
      this.sites[key].languages.length > 1
    ) {
      return this.sites[key].languages
    } else {
      return false
    }
  }

  get pathnameWithoutLanguageSubdirectory () {
    // Get pathname, check the first part (i.e. /pages), and make sure that it matches the expected
    // Shopify pathnames. If it doesn't, we assume it's a market subdirectory and remove it.
    const url = new URL(window.location.href)

    let pathname = url.pathname
    const pathnameSplit = pathname.split('/')
    if (!this.shopifyPathnames.includes(pathnameSplit[1])) {
      pathname = pathname.replace(`/${pathnameSplit[1]}`, '')
    }

    return pathname
  }

  get noChoiceMade () {
    return (
      window.localStorage.getItem(this.storageName) == null ||
      (window.localStorage.getItem(this.storageName) === undefined &&
        !Shopify.designMode)
    )
  }

  get noLocationStored () {
    return (
      window.localStorage.getItem(this.userLocationName) == null ||
      (window.localStorage.getItem(this.userLocationName) === undefined &&
        !Shopify.designMode)
    )
  }

  get storageName () {
    return 'BAO-store-portrait'
  }

  get userLocationName () {
    return 'BAO-location-portrait'
  }

  get sites () {
    return window.theme.sites
  }

  get countries () {
    return window.theme.countries
  }

  get shopifyPathnames () {
    return [
      'pages',
      'products',
      'collections',
      'blogs',
      'policies',
      'search',
      'account',
      'cart',
      '404',
    ]
  }
}

if (!customElements.get('shipping-selector')) {
  customElements.define('shipping-selector', ShippingSelector)
}
